/** Values that more than one tier needs to agree on. */
import type { MediaRef, NotifyKind, NotifyPrefs } from './types';

export const TIMEZONE = 'America/Denver';

/**
 * The three names the UI says out loud. Collection names and types keep their
 * old spelling: renaming a Firestore collection means moving every document,
 * and what a thing is called is not what it is.
 */
export const APP_NAME = 'Givi Den';
/** The prompted one, the thing a streak is made of. */
export const DAILY_NAME = 'GiviSnap';
/** What the family group is called, wherever the UI names it. */
export const CHAT_NAME = 'FamBam';

/**
 * Members who are not family: signed in and allowed everything, but left out
 * of anything the family counts together -- how many are in today, the family
 * streak, the longest streak and first in. A test account that never posts
 * would otherwise hold the family streak at nought for good.
 *
 * Not an allowlist. Access is decided in the three places CLAUDE.md lists, and
 * nothing here changes it. Compared lower-cased.
 */
export const NOT_FAMILY_EMAILS: readonly string[] = ['carterhustle20@gmail.com'];

/**
 * Testing on the live site without the family hearing about it.
 *
 * Anything a quiet sender puts in the chat is quiet: nobody outside the
 * audience is pushed about it or shown it, and a reply to it from inside the
 * audience is quiet too, so a snap back stays between the two test phones.
 * The rules spell QUIET_SENDERS and QUIET_AUDIENCE out again and must match.
 *
 * Only hidden, not secret. Every member can still read the documents; the
 * point is that nobody's phone buzzes or shows them, not that they are locked.
 */
export const QUIET_SENDERS: readonly string[] = ['carterhustle20@gmail.com'];
export const QUIET_AUDIENCE: readonly string[] = [
  'carterhustle20@gmail.com',
  'cgividen20@gmail.com',
];

const listed = (list: readonly string[], email: string | null | undefined) =>
  !!email && list.includes(email.toLowerCase());

export const isQuietSender = (email: string | null | undefined) =>
  listed(QUIET_SENDERS, email);

export const inQuietAudience = (email: string | null | undefined) =>
  listed(QUIET_AUDIENCE, email);

/** Whether this viewer is shown this message at all. */
export const canSee = (message: { quiet?: boolean }, email: string | null | undefined) =>
  !message.quiet || inQuietAudience(email);

/**
 * Whether a new message should be quiet: its sender is a quiet sender, or
 * someone in the audience who answers a quiet message or has switched on
 * quiet sending -- which is how a test started from Carter's own account
 * stays off the family's phones.
 */
export const shouldBeQuiet = (
  email: string | null | undefined,
  answering?: { quiet?: boolean } | null,
  quietSending = false,
) => isQuietSender(email) || (inQuietAudience(email) && (!!answering?.quiet || quietSending));

/** Firestore paths. */
export const COL = {
  messages: 'messages',
  dailies: 'dailies',
  dailyPosts: 'posts',
  dailyReplies: 'replies',
  users: 'users',
  usage: 'usage',
  uploads: 'uploads',
  errors: 'errors',
  typing: 'typing',
  /** pins/chat: the one message pinned over the family thread, for everyone. */
  pins: 'pins',
  /** recaps/{week}: the Sunday collage of the week's GiviSnaps (shared/recap.ts). */
  recaps: 'recaps',
  configFamily: 'config/family',
  configLimits: 'config/limits',
} as const;

/**
 * Typing indicators. While a draft has text in it, its writer refreshes a
 * stamp at most this often; a reader shows them as typing until this long
 * after the last refresh reached it. The gap between the two is what a
 * refresh on a slow connection has to arrive in, so the line does not blink.
 */
export const TYPING_REFRESH_MS = 3_000;
export const TYPING_SHOW_MS = 6_000;

/** R2 key prefixes. Lifecycle rules in r2-lifecycle.json key off these. */
export const PREFIX = {
  uploads: 'uploads/',
  media: 'media/',
  saved: 'saved/',
  /**
   * Chat snaps, which are for looking at once. Their own prefix so a
   * lifecycle rule can take them after SNAP_LIFETIME_MS and touch nothing
   * else, and so the gateway and the service worker can tell one from a
   * photo that is meant to be kept.
   */
  snaps: 'snaps/',
  /** Small copies of the week's GiviSnaps for the Sunday recap (shared/recap.ts). */
  recap: 'recap/',
} as const;

/**
 * How long a snap waits to be opened before it is gone for everyone, opened
 * or not. The app stops showing one at exactly this age; r2-lifecycle.json
 * deletes the bytes at the same age, which R2 carries out some time within
 * the following day.
 */
export const SNAP_LIFETIME_MS = 48 * 60 * 60 * 1000;

/**
 * How long after it was sent a snap can be looked at again -- by anyone who
 * has had their look, and by whoever sent it -- so an exchange can be gone
 * back over. Inside SNAP_LIFETIME_MS on purpose: the bytes must still be
 * there, and they go at 48 hours.
 */
export const SNAP_REPLAY_MS = 24 * 60 * 60 * 1000;

/**
 * The longest a video snap can be. It is held open with a finger down, and a
 * minute is a long time to hold one. createUpload refuses anything longer and
 * the transcoder cuts a snap clip at this regardless, since the length it is
 * told comes from the phone.
 */
export const SNAP_MAX_VIDEO_SECONDS = 15;

/**
 * How far past the limit a clip may run and still go. The app's own recorder
 * stops at the limit on a fifth-of-a-second tick, and a file's stated length
 * carries a little container slack, so a full-length clip measures a touch
 * over 15. createUpload once refused anything over 15 exactly while the phone
 * let it through, and the clip sat in the queue retrying for ever. The
 * transcoder still cuts at SNAP_MAX_VIDEO_SECONDS, so the extra never shows.
 */
export const SNAP_VIDEO_TOLERANCE_SECONDS = 1;

/**
 * Whether a video snap is too long to go, by the one rule the phone and
 * createUpload both apply. A length that is not a number is too long: leaving
 * it out must not be a way past the limit.
 */
export function snapClipTooLong(durationSec: number | undefined): boolean {
  return (
    typeof durationSec !== 'number' ||
    !Number.isFinite(durationSec) ||
    durationSec > SNAP_MAX_VIDEO_SECONDS + SNAP_VIDEO_TOLERANCE_SECONDS
  );
}

/** A snap's caption, as long as a GiviSnap's. */
export const SNAP_CAPTION_MAX = 140;
/** Emojis on one snap: enough to say something, few enough to still see the picture. */
export const SNAP_STICKERS_MAX = 12;

/** Gateway session cookie. Set by the Worker, never read by JS. */
export const SESSION_COOKIE = 'gs';
export const SESSION_TTL_SECONDS = 12 * 60 * 60;

/** Presigned PUT lifetime. */
export const UPLOAD_URL_TTL_SECONDS = 15 * 60;

/** How long a pending upload may sit before reconcileUploads adopts it. */
export const UPLOAD_RECONCILE_AFTER_MINUTES = 30;

/**
 * A daily clip is meant to be a glimpse, not a film, and every second of it is
 * storage against the monthly budget. Chat keeps the looser maxVideoSeconds.
 */
export const MAX_DAILY_VIDEO_SECONDS = 15;

/**
 * Whether a GiviSnap clip is too long, with the same second's give a snap
 * has: a clip recorded to the limit comes out a fraction over it, and refused
 * by createUpload it would sit in the queue as the snaps once did. Nothing on
 * the phone sends a GiviSnap clip today; this keeps the trap shut if one
 * does. A missing length passes, as it always has here -- the transcoder
 * still cuts at the limit.
 */
export function dailyClipTooLong(durationSec: number | undefined): boolean {
  return (durationSec ?? 0) > MAX_DAILY_VIDEO_SECONDS + SNAP_VIDEO_TOLERANCE_SECONDS;
}

/**
 * Which day a run is counting at this moment, which is not the calendar day.
 *
 * A streak ends when the next reminder fires rather than at midnight. So
 * everything between midnight and today's prompt still belongs to yesterday:
 * answer late that night or first thing the next morning, any time before the
 * reminder, and the run holds.
 *
 * Which also means the answer changes during the day for one reason only —
 * the prompt firing — and both the screen and the upload path have to agree
 * about it, so neither works it out for itself.
 */
export function streakDayFor(day: string, promptFired: boolean): string {
  return promptFired ? day : previousDay(day);
}

/**
 * Whether this person has already answered the prompt that is live now, which
 * is the only thing that closes the GiviSnap camera.
 *
 * The gate is the prompt, not the clock. Asking "is it inside today's window"
 * would shut the door at midnight on someone who has not answered yet, and the
 * whole point of moving the cutoff off midnight was that they still can.
 *
 * Their posts filed under the streak day, and nothing else. A GiviSnap is
 * filed under the prompt it answers, so that is exactly the question. The
 * counter on the person cannot answer it: a post taken this morning answers
 * last night's prompt but also credits today, so the counter jumps to the
 * calendar day. Reading it said "waiting" all morning to someone who had
 * posted, and then "answered" for today's prompt the moment it fired, to
 * someone who had not. An extra is never an answer, or opening the app would
 * count as one.
 */
export function answeredPrompt(
  posts: readonly { day: string; data: { authorUid: string; kind?: string } }[],
  uid: string,
  streakDay: string,
): boolean {
  return posts.some(
    (post) =>
      post.day === streakDay &&
      post.data.authorUid === uid &&
      post.data.kind !== 'extra',
  );
}

/**
 * A streak is consecutive days carrying at least one daily. It is stored on
 * the person and stamped with the day it last moved, because dailies delete
 * themselves and cannot be counted after the fact.
 *
 * Nothing runs to break it. A run whose last day is older than the one before
 * the day being counted is simply read as nought, which cannot drift the way a
 * scheduled reset can — and it is why moving the cutoff off midnight took no
 * new job, only a different answer to which day is being counted.
 *
 * `today` is the streak day from streakDayFor, not the date.
 */
export function streakOf(
  user: { streak?: number; lastDailyDay?: string } | undefined,
  today: string,
  yesterday: string,
  /**
   * Whether they have a prompted post today. The stored counter is written by
   * the upload path, so it is briefly behind the photo it is counting — and it
   * was never written at all for anything posted before streaks existed. The
   * posts are the ground truth for today, so they win.
   */
  postedToday = false,
): number {
  const stored = user?.streak ?? 0;
  const last = user?.lastDailyDay;

  /*
   * Already counted, or counted past here.
   *
   * Ahead is possible and legitimate: a GiviSnap taken this morning answers
   * last night's prompt and also happened today, so it credits both — and
   * until today's prompt fires, the day being counted is still last night's
   * while the mark has already moved on to today. Comparing for equality read
   * that as a broken run and showed nought to the one person who had posted
   * most recently.
   */
  if (last && last >= today) return stored;

  // Posted, not yet counted: carry yesterday's run, or start a new one.
  if (postedToday) return (last === yesterday ? stored : 0) + 1;

  // Nothing yet for this one. The run before it stands until the reminder
  // fires and the day being counted moves on.
  return last === yesterday ? stored : 0;
}

/** The day before a yyyy-mm-dd key, in the scheduling zone. */
export function previousDay(day: string, timeZone: string = TIMEZONE): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  // Noon UTC keeps the arithmetic clear of any zone's midnight.
  const at = new Date(Date.UTC(y, m - 1, d, 12));
  at.setUTCDate(at.getUTCDate() - 1);
  return dayKey(at, timeZone);
}

/*
 * A photo is sent as the camera wrote it -- HDR and wide colour intact -- only
 * while it is small enough to go up quickly. Past this it is redrawn, at most
 * IMAGE_MAX_EDGE on its long side: a 12-megapixel camera file of 6 to 11 MB
 * was what made uploads slow, and the family chose speed over HDR for those.
 */
/** The longest a chat message's words may be, caption or not; the rules say 2000 too. */
export const MESSAGE_TEXT_MAX = 2000;

export const IMAGE_KEEP_AS_IS_BYTES = 2.5e6;
export const IMAGE_MAX_EDGE = 3000;
export const IMAGE_JPEG_QUALITY = 0.85;

/*
 * The blurred stand-in kept on a photo's ref and shown while the photo itself
 * comes, blurred by nothing more than being drawn this small and stretched
 * back up. A PNG: at this size a JPEG is mostly header, over a kilobyte, and
 * its 8-pixel blocks smear the colours; a PNG is a third the size, exact, and
 * stretches smooth.
 */
export const BLUR_EDGE = 16;
export const BLUR_PREFIX = 'data:image/png;base64,';
/** More than any honest one, so a ref cannot be made to carry anything else. */
export const BLUR_MAX_CHARS = 3000;

/*
 * The small copy of a photo, drawn wherever the photo is one of many or a
 * part of a screen: the feeds, the chat, the grids, the thumbnails. The camera
 * file -- up to 2.5 MB, or 3000 pixels redrawn -- is only fetched when a
 * photo is opened on its own, saved or copied. A tenth the bytes, give or
 * take, and still sharp at the width of a phone.
 *
 * Made on the phone and sent beside the photo. Only worth one when the photo
 * is much bigger than this; below SMALL_WORTH_RATIO times it, the photo is
 * drawn as it is.
 */
export const SMALL_EDGE = 1080;
export const SMALL_JPEG_QUALITY = 0.82;
export const SMALL_WORTH_RATIO = 1.25;

/**
 * Where a photo's small copy lives: beside it, under the same prefix, so it
 * expires, is served and is kept exactly as the photo is. Null for a key that
 * is not a JPEG photo's.
 */
export function smallKeyFor(key: string): string | null {
  return /\.jpg$/.test(key) ? key.replace(/\.jpg$/, '-small.jpg') : null;
}

/**
 * The ref to draw where a photo is one of many or part of a screen: its small
 * copy, where it has one, and otherwise the photo itself. Opened on its own,
 * saved or copied, a photo is always the photo.
 */
export function shownMedia(media: MediaRef): MediaRef {
  return media.small ? { ...media, key: media.small } : media;
}

/** Whether a stand-in is the small PNG it says it is, and nothing more. */
export function isBlur(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith(BLUR_PREFIX) &&
    value.length <= BLUR_MAX_CHARS &&
    /^[A-Za-z0-9+/]+=*$/.test(value.slice(BLUR_PREFIX.length))
  );
}

export const DEFAULT_LIMITS = {
  monthlyUploadBytes: 8e9,
  warnAtFraction: 0.75,
  maxFileBytes: 200e6,
  maxVideoSeconds: 90,
  maxAudioSeconds: 120,
  retentionDays: 2,
  uploadsDisabled: false,
  dailyWindowStart: '09:00',
  dailyWindowEnd: '21:00',
  dailyOnTimeMinutes: 5,
  dailyRetakeMinutes: 5,
} as const;

/**
 * Dailies only. Chat messages are written with `expireAt: null` and their
 * bytes land under `saved/`, so nothing below reaches them.
 *
 * Three numbers are coupled and must move together:
 *
 *   1. r2-lifecycle.json deletes objects under media/ after 3 days.
 *   2. firestore.rules caps a client-set expireAt at 3 days out.
 *   3. config/limits.retentionDays, which is what the app actually uses.
 *
 * Two days rather than one because a GiviSnap has to still be there when the
 * next one starts, and consecutive prompts can be thirty-six hours apart —
 * nine in the morning one day, nine at night the next. A day of retention let
 * one posted early vanish before the prompt that replaces it had fired.
 *
 * A document must expire *before* its bytes, or a post renders as a broken
 * image. So retention is clamped here rather than trusted: a stale or
 * mistaken config value would otherwise be rejected outright by the rules —
 * which is exactly how sending a message broke once — or worse, accepted and
 * left pointing at deleted media.
 *
 * Raising retention means raising all three, in that order.
 */
/**
 * How long a fault is kept.
 *
 * Longer than a photograph, because the point of it is to still be there on
 * the evening somebody finally mentions that the app did something odd on
 * Tuesday. Short enough that the collection cannot grow without end.
 */
export const ERROR_RETENTION_DAYS = 14;

export const MAX_RETENTION_DAYS = 2;

export function retentionDaysOrDefault(days: number | undefined): number {
  if (!Number.isFinite(days) || (days ?? 0) < 1) return MAX_RETENTION_DAYS;
  return Math.min(Math.floor(days as number), MAX_RETENTION_DAYS);
}

/**
 * Everything on. Someone who installs the app and never opens settings should
 * still be told the daily window opened, which is most of the point.
 */
export const DEFAULT_NOTIFY_PREFS: NotifyPrefs = {
  dailyPrompt: true,
  dailyPosts: true,
  storyPosts: true,
  messages: true,
  snapBacks: true,
  mentions: true,
  replies: true,
  streakWarning: true,
};

/** A missing key means the default, so a new kind does not arrive switched off. */
export function notifyPrefsOrDefault(prefs: Partial<NotifyPrefs> | undefined): NotifyPrefs {
  return { ...DEFAULT_NOTIFY_PREFS, ...(prefs ?? {}) };
}

/** Whether a push of this kind should reach someone with these preferences. */
export function wantsPush(prefs: Partial<NotifyPrefs> | undefined, kind: NotifyKind): boolean {
  if (kind === 'system') return true;
  return notifyPrefsOrDefault(prefs)[kind];
}

/** yyyy-mm-dd for a Date in the scheduling zone. */
export function dayKey(d: Date, timeZone: string = TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** yyyy-mm for usage counters, in the scheduling zone. */
export function monthKey(d: Date, timeZone: string = TIMEZONE): string {
  return dayKey(d, timeZone).slice(0, 7);
}

/** The public URL for a stored object. */
export function mediaUrl(gateway: string, key: string): string {
  return `${gateway.replace(/\/$/, '')}/media/${key
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;
}
